import {
  HttpContextToken,
  HttpErrorResponse,
  type HttpEvent,
  type HttpHandlerFn,
  type HttpInterceptorFn,
  type HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';

import { catchError, type Observable, throwError } from 'rxjs';

import { environment } from '@env';

import { AppPath } from '@kernel';

import { AuthStore } from './auth.store';

export const SKIP_AUTH = new HttpContextToken<boolean>(() => false);

// Credentials and the 401-driven logout flow only apply to our own backend — never to
// third-party requests (analytics, maps, widgets, ...) issued through HttpClient.
const TRUSTED_ORIGIN = new URL(environment.userBaseUrl).origin;

const isTrustedRequest = (url: string): boolean => {
  try {
    return new URL(url).origin === TRUSTED_ORIGIN;
  } catch {
    return false;
  }
};

export const authInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
  const authStore = inject(AuthStore);
  const router = inject(Router);

  const trusted = isTrustedRequest(req.url);
  const skipAuth = req.context.get(SKIP_AUTH);
  const request = trusted && !skipAuth ? req.clone({ withCredentials: true }) : req;

  return next(request).pipe(
    catchError((error: unknown): Observable<never> => {
      if (
        trusted &&
        !skipAuth &&
        error instanceof HttpErrorResponse &&
        error.status === 401 &&
        authStore.initialized()
      ) {
        authStore.clearAuth();

        // Guard against a redirect loop when the 401 came from the login flow itself.
        if (!router.url.startsWith(AppPath.Login)) void router.navigateByUrl(AppPath.Login);
      }

      return throwError(() => error);
    }),
  );
};
